import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { DragEvent, ReactNode, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { dayLabel } from '../design/dates';
import { useMovable } from '../nook/useMovable';
import { REF_MIME } from '../nook/dnd';
import type { Row } from '../nook/nook';
import { CanvasMark } from './CanvasMark';
import { pageSize } from './Hang';
import { useLeaving } from './Presence';
import './tray.css';

export type TrayKind = 'drafts' | 'shelf' | 'recordings' | 'notes' | 'arcs' | 'nooks';

export const TRAY_LABEL: Record<TrayKind, string> = {
  drafts: 'Drafts',
  shelf: 'Shelf',
  recordings: 'Recordings',
  notes: 'Notes',
  arcs: 'Tasks',
  nooks: 'Nooks',
};

/** Wide enough for the desk's tabs, all seven at once; `--trayW` in tray.css says the same. */
export const TRAY_WIDTH = 440;
const LIFT_KEY = 'tray.lifted';
const DAY = 24 * 60 * 60 * 1000;
export const RECENT_DAYS = 3;

/** Docked or lifted off, kept in this browser alone: the page reads the same without it. */
export function useTrayLifted(): [boolean, () => void] {
  const [lifted, setLifted] = useState(() => {
    try {
      return localStorage.getItem(LIFT_KEY) === '1';
    } catch {
      return false;
    }
  });
  const toggle = () => {
    const next = !lifted;
    setLifted(next);
    try {
      if (next) localStorage.setItem(LIFT_KEY, '1');
      else localStorage.removeItem(LIFT_KEY);
    } catch {
      // Not kept; it stays as it is for this look.
    }
  };
  return [lifted, toggle];
}

export interface TrayEntry {
  row: Row;
  /** When it was captured or last touched; undated ones fall to the foot, among the older. */
  at: Date | null;
  /** On the page's canvas already: it stays, says so, and is not carried. */
  placed: boolean;
}

/** The entries of the newest [RECENT_DAYS] distinct days, in the order given; the rest are older.
 *  Where none is dated, all are shown. */
export function lastDays<E extends { at: Date | null }>(entries: readonly E[], days = RECENT_DAYS): { shown: E[]; older: E[] } {
  const dayOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const distinct = [...new Set(entries.flatMap((e) => (e.at ? [dayOf(e.at)] : [])))].sort((a, b) => b - a).slice(0, days);
  if (distinct.length === 0) return { shown: [...entries], older: [] };
  const keep = new Set(distinct);
  const shown = entries.filter((e) => e.at && keep.has(dayOf(e.at)));
  return { shown, older: entries.filter((e) => !(e.at && keep.has(dayOf(e.at)))) };
}

function dayName(at: Date, now: Date): string {
  const days = Math.round((new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() - new Date(at.getFullYear(), at.getMonth(), at.getDate()).getTime()) / DAY);
  return days === 0 ? 'Today' : days === 1 ? 'Yesterday' : dayLabel(at);
}

export function carry(event: DragEvent<HTMLElement>, row: Row) {
  event.dataTransfer.setData(REF_MIME, JSON.stringify({ ref: row.ref.ref, anchors: row.ref.anchors }));
  event.dataTransfer.effectAllowed = 'copyMove';
}

/**
 * What the tray lists: real cards, a day at a time, newest first — the last few days of a kind,
 * and what a search finds across the whole vault. A card already on the canvas stays in its place
 * and wears the canvas mark; the rest are carried out by a drag, and a press opens their details.
 */
export function TrayList({
  entries,
  query,
  recent,
  older,
  carrying,
  renderCard,
  empty,
}: {
  entries: TrayEntry[];
  query: string;
  /** Whether the entries are days to trim to the last few. */
  recent: boolean;
  /** Entries a page has already trimmed, with the way to bring them in. */
  older?: { count: number; onMore: () => void };
  /** Whether the canvas can be put on: a card is carried only then. */
  carrying: boolean;
  renderCard: (entry: TrayEntry) => ReactNode;
  /** What an empty list says, where it is not empty for want of a match. */
  empty?: string;
}) {
  const [all, setAll] = useState(false);
  const searching = query.trim() !== '';
  const trimmed = recent && !searching && !all ? lastDays(entries) : { shown: entries, older: [] as TrayEntry[] };
  const now = new Date();

  const items: ReactNode[] = [];
  let day = '';
  for (const entry of trimmed.shown) {
    if (recent && !searching) {
      const name = entry.at ? dayName(entry.at, now) : '';
      if (name !== day) {
        day = name;
        if (name) items.push(<h3 key={`day-${name}`} className="tray-day">{name}</h3>);
      }
    }
    items.push(
      <div
        key={entry.row.ref.ref + '|' + entry.row.ref.anchors.join(',')}
        className={`tray-card desk-tray-card${entry.placed ? ' is-placed' : ''}`}
        data-tray-card
        draggable={carrying && !entry.placed}
        onDragStart={(e) => carry(e, entry.row)}
      >
        {renderCard(entry)}
      </div>,
    );
  }
  const more = older ?? (trimmed.older.length > 0 ? { count: trimmed.older.length, onMore: () => setAll(true) } : null);

  return (
    <>
      {searching && <h3 className="tray-day">Matching “{query.trim()}”</h3>}
      {items}
      {entries.length === 0 && (searching ? <p className="tray-none">Nothing matches</p> : empty && <p className="tray-none">{empty}</p>)}
      {more && more.count > 0 && (
        <button type="button" className="tray-older desk-tray-older" onClick={more.onMore}>
          {more.count} older
        </button>
      )}
    </>
  );
}

const Glass = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
    <circle cx="10.5" cy="10.5" r="6" />
    <path d="M15 15l5 5" />
  </svg>
);
const Lift = ({ docked }: { docked: boolean }) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {docked ? <path d="M4.5 4.5h15v15h-15zM14.5 4.5v15" /> : <path d="M9 4.5h10.5V15M19.5 4.5L10 14M5 8.5v10.5h10.5" />}
  </svg>
);
const Close = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

/**
 * Browse: the drawer beside a page's canvas, looked through and added to the canvas from. Docked,
 * it joins the table as one surface; lifted off, it floats over the page as a tile, moved by its
 * head. Its tabs, on their own row under the head, are the kinds it holds; the glass unfolds a
 * search at the top of the list.
 */
export function Tray({
  kinds,
  kind,
  onKind,
  lifted,
  onToggleLift,
  onClose,
  scope,
  query,
  onQuery,
  full = false,
  maxHeight,
  className,
  trayRef,
  children,
}: {
  kinds: TrayKind[];
  kind: TrayKind;
  onKind: (kind: TrayKind) => void;
  lifted: boolean;
  onToggleLift: () => void;
  onClose: () => void;
  /** A nook's tray lists what it holds (Here) or the whole vault (All). */
  scope?: { all: boolean; onChange: (all: boolean) => void };
  query: string;
  onQuery: (query: string) => void;
  /** Full canvas: there is no page beside the table, so a docked tray floats at the right edge,
   *  staying put until it is lifted off. */
  full?: boolean;
  maxHeight?: number;
  className?: string;
  trayRef?: (el: HTMLElement | null) => void;
  children: ReactNode;
}) {
  const [searching, setSearching] = useState(query !== '');
  const field = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (searching) field.current?.focus();
  }, [searching]);
  useEffect(() => {
    setSearching(false);
    onQuery('');
    // A new kind folds the search back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);
  const floating = lifted || full;
  const { offset, handle } = useMovable(lifted);
  const [host, setHost] = useState<Element | null>(null);
  /** Held still in full canvas, it stands beside the table's corner tools rather than over them. */
  const [pinnedAt, setPinnedAt] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    if (!floating) return;
    const main = document.querySelector('.nook-main');
    setHost(main);
    const corner = document.querySelector('.table.is-full .table-corner')?.getBoundingClientRect();
    const origin = main?.getBoundingClientRect();
    setPinnedAt(corner && origin && corner.width > 0 ? { left: corner.left - origin.left - TRAY_WIDTH - 12, top: corner.top - origin.top } : null);
  }, [floating, full]);

  const leaving = useLeaving();
  const toggleSearch = () => {
    if (searching) onQuery('');
    setSearching(!searching);
  };

  const body = (
    <aside
      ref={trayRef}
      className={`tray${floating ? ' is-lifted' : ' is-docked'}${floating && !lifted ? ' is-pinned' : ''}${leaving ? ' is-leaving' : ''}${className ? ` ${className}` : ''}`}
      aria-label="Browse"
      data-hang-keeps
      inert={leaving}
      style={lifted ? liftedAt(offset) : floating ? pinned(pinnedAt) : maxHeight ? { maxHeight } : undefined}
    >
      <div className="tray-head" {...handle}>
        {lifted && <span className="tray-grip" aria-hidden="true" />}
        <span className="tray-title">Browse</span>
        <span className="tray-spacer" />
        <span className="tray-tools">
          <button type="button" className={`tray-tool${searching ? ' is-on' : ''}`} aria-label="Search" aria-pressed={searching} title="Search" onClick={toggleSearch}>
            <Glass />
          </button>
          <button type="button" className="tray-tool" aria-label={lifted ? 'Dock' : 'Lift off'} title={lifted ? 'Dock' : 'Lift off'} onClick={onToggleLift}>
            <Lift docked={lifted} />
          </button>
          <button type="button" className="tray-tool" aria-label="Close" title="Close" onClick={onClose}>
            <Close />
          </button>
        </span>
      </div>
      <div className="tray-row">
        <span className="tray-words" role="tablist" aria-label="Kinds">
          {kinds.map((k) => (
            <button key={k} type="button" role="tab" aria-selected={k === kind} className={`tray-word${k === kind ? ' is-on' : ''}`} onClick={() => onKind(k)}>
              {TRAY_LABEL[k]}
            </button>
          ))}
        </span>
        {scope && (
          <div className="tray-scope" role="group" aria-label="Scope">
            <button type="button" aria-pressed={!scope.all} className={!scope.all ? 'is-on' : undefined} onClick={() => scope.onChange(false)}>
              Here
            </button>
            <button type="button" aria-pressed={scope.all} className={scope.all ? 'is-on' : undefined} onClick={() => scope.onChange(true)}>
              All
            </button>
          </div>
        )}
      </div>
      <div className="tray-body">
        {searching && (
          <label className="tray-search">
            <Glass />
            <input
              ref={field}
              type="search"
              value={query}
              placeholder={`Search ${TRAY_LABEL[kind].toLowerCase()}`}
              aria-label={`Search ${TRAY_LABEL[kind].toLowerCase()}`}
              onChange={(e) => onQuery(e.target.value)}
            />
          </label>
        )}
        {children}
      </div>
    </aside>
  );
  if (!floating) return body;
  return host ? createPortal(body, host) : null;
}

function pinned(at: { left: number; top: number } | null) {
  if (!at) return liftedAt({ x: 0, y: 0 });
  const win = pageSize();
  return { position: 'absolute' as const, left: Math.max(8, at.left), top: at.top, maxHeight: Math.max(240, win.h - at.top - 16) };
}

function liftedAt(offset: { x: number; y: number }) {
  const win = pageSize();
  return {
    position: 'absolute' as const,
    left: Math.max(8, win.w - TRAY_WIDTH - 24) + offset.x,
    top: 52 + offset.y,
    maxHeight: Math.max(240, win.h - 52 - 16),
  };
}

/** A tray card's ⋮: open it, or file it away. It closes on a press anywhere else. */
export function TrayCardMenu({
  at,
  onOpen,
  onArchive,
  onClose,
}: {
  at: { x: number; y: number };
  onOpen: () => void;
  /** Absent where the card cannot be filed away from here. */
  onArchive?: () => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const away = (e: PointerEvent) => {
      if (!(e.target as HTMLElement).closest('.plane-menu')) onClose();
    };
    const id = setTimeout(() => document.addEventListener('pointerdown', away));
    return () => {
      clearTimeout(id);
      document.removeEventListener('pointerdown', away);
    };
  }, [onClose]);
  const run = (fn: () => void) => () => {
    fn();
    onClose();
  };
  return (
    <div className="plane-menu" style={{ left: at.x, top: at.y }} role="menu">
      <button type="button" role="menuitem" onClick={run(onOpen)}>
        Open
      </button>
      {onArchive && (
        <button type="button" role="menuitem" onClick={run(onArchive)}>
          Archive
        </button>
      )}
    </div>
  );
}

/** Browse, among the table's corner tools: opens and closes the tray, held down while it is open. */
export function TrayBrowse({ open, onPress, buttonRef }: { open: boolean; onPress: () => void; buttonRef?: RefObject<HTMLButtonElement | null> }) {
  return (
    <button ref={buttonRef} type="button" className={`table-add${open ? ' is-on' : ''}`} aria-pressed={open} onClick={onPress}>
      <Cards />
      Browse
    </button>
  );
}

const Cards = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="7" width="12.5" height="13" rx="2.5" />
    <path d="M8 4h9a3 3 0 0 1 3 3v9" />
  </svg>
);

export { CanvasMark };
